import type { Company } from "@repo/app-types/entities/company";
import {
  getDialingPhoneNumber,
  getDisplayPhoneNumber,
} from "@repo/app-types/helpers/utilities";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { showToast, ToastVariant } from "@repo/ui/toast/toast";
import { IconCheck } from "@tabler/icons-react";
import copy from "copy-to-clipboard";
import { type FC, useState } from "react";
import { useAppSelector } from "../../../hooks/hook.js";
import { selectPhoneNumberAndExtensionForProject } from "../../phoneNumbers/phoneNumbersSlice.js";

interface PhoneNumberDisplaySectionProps {
  projectId: string;
  company: Company;
}

export const PhoneNumberDisplaySection: FC<PhoneNumberDisplaySectionProps> = ({
  projectId,
  company,
}: PhoneNumberDisplaySectionProps) => {
  const [isCopyingPhoneNumber, setIsCopyingPhoneNumber] = useState(false);

  const phoneNumberAndExtension = useAppSelector(
    selectPhoneNumberAndExtensionForProject(projectId),
  );

  const phoneNumberForDisplay = phoneNumberAndExtension
    ? getDisplayPhoneNumber(
        phoneNumberAndExtension.phoneNumber.number,
        phoneNumberAndExtension.extension,
      )
    : null;
  const phoneNumberForDialing = phoneNumberAndExtension
    ? getDialingPhoneNumber(
        phoneNumberAndExtension.phoneNumber.number,
        phoneNumberAndExtension.extension,
      )
    : null;

  if (!phoneNumberForDisplay || !phoneNumberForDialing) {
    return (
      <div className="max-w-[350px] text-sm text-muted-foreground">
        No phone number found - reach out to{" "}
        {company.settings.whitelabel_domain
          ? "support "
          : "support@alpharun.com "}
        to get a phone number.
      </div>
    );
  }

  const handleCopyPhoneNumber = () => {
    setIsCopyingPhoneNumber(true);
    copy(
      `Call ${phoneNumberForDisplay}\nOr on mobile tap to call: ${phoneNumberForDialing}`,
    );
    setTimeout(() => {
      setIsCopyingPhoneNumber(false);
    }, 4000);
    showToast({
      title: `Dial instructions copied to clipboard`,
      variant: ToastVariant.Success,
    });
  };

  return (
    <>
      <div className="mt-1 flex flex-row space-x-3">
        <Button
          variant={ButtonVariant.Secondary}
          onClick={handleCopyPhoneNumber}
        >
          <div className="flex flex-row space-x-2">
            <span className="text-sm font-medium text-blue-600">
              {`${phoneNumberForDisplay}`}
            </span>
            {isCopyingPhoneNumber ? (
              <IconCheck className="h-5 w-5 text-blue-600" />
            ) : null}
          </div>
        </Button>
      </div>
    </>
  );
};
